import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Ship } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, SectionHead, Wrap } from "./Motion";

const sym = { EUR: "€", USD: "$", GBP: "£" };
const fmt = (v, c) => `${sym[c]}${Number(v).toLocaleString()}`;
const inputCls = "w-full bg-[#0B1630] border border-white/12 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F8F6F0] transition-colors";

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#6F7C97]">{label}</span><div className="mt-2">{children}</div></label>
);

export const Transport = () => {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({ destination: "usa", horses: 1, insurance: true, horse_value: 50000, cem: false, currency: "EUR" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/transport/rates").then((r) => setRates(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const northAmerica = ["usa", "canada"].includes(form.destination);

  const calc = async () => {
    setLoading(true);
    try { const r = await api.post("/transport/estimate", { ...form, horses: Number(form.horses), horse_value: Number(form.horse_value) }); setRes(r.data); }
    finally { setLoading(false); }
  };

  return (
    <section id="transport" className="relative py-20 lg:py-36 bg-[#0F1D3D]" data-testid="transport-section">
      <Wrap className="grid lg:grid-cols-12 gap-10 lg:gap-20">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="Global transit · Indicative estimator" id="transport" title="Know the landed cost before you raise your hand." sub="Flights, road, papers and quarantine from Ballypatrick to your yard — so the only surprise on the night is the horse." />
          <Reveal delay={0.25}>
            <ul className="mt-8 space-y-3 text-sm text-[#AEB7C9]">
              <li className="flex gap-3"><Plane size={16} className="text-[#6FAE55] mt-0.5 shrink-0" /> Air via Liège or Amsterdam; USDA quarantine included for North America.</li>
              <li className="flex gap-3"><Ship size={16} className="text-[#6FAE55] mt-0.5 shrink-0" /> Road and ferry to the UK and Europe, door to stable within 48 hours.</li>
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="lg:col-span-7">
          <div className="bg-[#0B1630] border border-[#D4AF37]/20 p-5 sm:p-8 lg:p-10" data-testid="transport-calculator">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Destination">
                <select className={inputCls} value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="ballypatrick-transport-select-destination">
                  {rates && Object.entries(rates.routes).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Number of horses">
                <select className={inputCls} value={form.horses} onChange={(e) => set("horses", e.target.value)} data-testid="ballypatrick-transport-select-count">
                  {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Approx. purchase value (€)">
                <input type="number" inputMode="numeric" min="0" step="1000" className={inputCls} value={form.horse_value} onChange={(e) => set("horse_value", e.target.value)} data-testid="ballypatrick-transport-input-value" />
              </Field>
              <Field label="Show estimate in">
                <div className="flex border border-white/12">
                  {["EUR", "USD", "GBP"].map((c) => (
                    <button key={c} onClick={() => set("currency", c)} data-testid={`ballypatrick-transport-currency-${c.toLowerCase()}`} className={`flex-1 py-3 text-xs tracking-[0.15em] transition-colors ${form.currency === c ? "bg-[#D4AF37] text-[#0B1630]" : "text-[#AEB7C9] hover:text-[#F8F6F0]"}`}>{c}</button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:gap-6">
              <label className="flex items-center gap-3 text-sm text-[#AEB7C9] cursor-pointer"><input type="checkbox" checked={form.insurance} onChange={(e) => set("insurance", e.target.checked)} className="accent-[#D4AF37] h-4 w-4" data-testid="ballypatrick-transport-toggle-insurance" /> Transit insurance (2%)</label>
              {northAmerica && <label className="flex items-center gap-3 text-sm text-[#AEB7C9] cursor-pointer"><input type="checkbox" checked={form.cem} onChange={(e) => set("cem", e.target.checked)} className="accent-[#D4AF37] h-4 w-4" data-testid="ballypatrick-transport-toggle-cem" /> Mare or stallion (CEM quarantine)</label>}
            </div>
            <button onClick={calc} disabled={loading || !rates} className="btn-gold mt-7 w-full disabled:opacity-60" data-testid="ballypatrick-transport-calculate-btn">{loading ? "Calculating…" : "Estimate landed cost"}</button>
            <AnimatePresence>
              {res && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid="ballypatrick-transport-result-card">
                  <div className="mt-7 pt-7 border-t border-[#D4AF37]/20">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#6F7C97]">Ballypatrick → {res.destination} · {res.days}</p>
                    <p className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl text-[#D4AF37]" data-testid="transport-total">{fmt(res.total_low, res.currency)} – {fmt(res.total_high, res.currency)}</p>
                    <ul className="mt-5 space-y-2.5">
                      {res.lines.map((l, i) => (
                        <li key={i} className="flex justify-between gap-4 text-xs sm:text-sm border-b border-white/5 pb-2.5"><span className="text-[#AEB7C9]">{l.label}</span><span className="text-[#F8F6F0] whitespace-nowrap">{fmt(l.low, res.currency)}{l.low !== l.high && ` – ${fmt(l.high, res.currency)}`}</span></li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs text-[#6F7C97] italic">{res.disclaimer}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </Wrap>
    </section>
  );
};
